import type { KskPayment } from "@talentkontor/domain";
import { money } from "../../../lib/format";
import { t } from "../../../lib/sprache";

/**
 * Künstlersozialabgabe im Umstiegsjahr (PRAXIS-R2-3): Monate, die beim Übernehmen des Bestands als „bezahlt“ markiert wurden
 * (deal_periods.imported_state = 'paid'), sind im bisherigen System an den Creator ausgezahlt. Hier gibt es dafür weder
 * Rechnung noch Auszahlungstag, deshalb zählen sie nicht mit. Damit die Liste nicht vollständig wirkt, nennt „Bitte prüfen“
 * je Creator die übernommenen Monate und den ungefähren Creator-Anteil (Honorar abzüglich des Satzes der Kooperation).
 */
export interface KskImportedPeriod { deal_id: string; month: string; amount_cents: number | null; imported_state: string | null }
export interface KskImportedDeal { id: string; talent_id: string; commission_bp: number | null; currency: string | null }

export function kskImportedPayments(periods: readonly KskImportedPeriod[], deals: readonly KskImportedDeal[], year: number,
  talent: (id: string) => Pick<KskPayment, "talentName" | "companyName" | "legalForm" | "country">): KskPayment[] {
  const dealOf = new Map(deals.map((d) => [d.id, d]));
  const byTalent = new Map<string, { months: string[]; shareCents: number; currency: string }>();
  for (const p of periods) {
    if (p.imported_state !== "paid" || !p.month.startsWith(`${year}-`)) continue;
    const d = dealOf.get(p.deal_id);
    if (!d) continue;
    const share = Math.round((p.amount_cents ?? 0) * (10000 - (d.commission_bp ?? 0)) / 10000);
    const cur = byTalent.get(d.talent_id) ?? { months: [], shareCents: 0, currency: d.currency ?? "EUR" };
    cur.months.push(p.month.slice(0, 7));
    cur.shareCents += share;
    byTalent.set(d.talent_id, cur);
  }
  return [...byTalent.entries()].map(([talentId, x]) => {
    const months = [...new Set(x.months)].sort();
    const span = months.length === 1 ? fmtMonth(months[0]!) : `${fmtMonth(months[0]!)}–${fmtMonth(months[months.length - 1]!)}`;
    const n = months.length;
    return {
      talentId, ...talent(talentId), invoiceNumber: null, paidOn: `${year}-01-01`, lines: [],
      checkOnly: t(`Aus dem bisherigen System übernommen: ${n === 1 ? "1 Monat" : `${n} Monate`} (${span}) als ausgezahlt, rund ${money(x.shareCents, x.currency)} Creator-Anteil. Nicht gezählt; Auszahlungen vor dem Umstieg bitte ergänzen.`,
        `Taken over from the previous system: ${n === 1 ? "1 month" : `${n} months`} (${span}) as paid out, about ${money(x.shareCents, x.currency)} creator share. Not counted; please add payouts made before the switch.`),
    };
  });
}

const fmtMonth = (ym: string) => `${ym.slice(5, 7)}/${ym.slice(0, 4)}`;

/** Hinweis unter der Auswertung, sobald übernommene, schon ausgezahlte Monate im Jahr liegen */
export function kskImportedNote(count: number): string | null {
  return count > 0
    ? t("Im Umstiegsjahr fehlen Auszahlungen aus dem bisherigen System: Übernommene Monate mit Stand „bezahlt“ stehen unter „Bitte prüfen“ und zählen nicht mit. Bitte die Entgelte aus dem bisherigen System für die Meldung ergänzen.",
      "In the year of the switch, payouts from the previous system are missing: taken-over months marked “paid” appear under “Please check” and are not counted. Please add the fees from the previous system for the report.")
    : null;
}
